import { z } from 'zod';
import { dataHoraSchema, slugSchema } from './comum.js';
/**
 * ============================================================================
 * O SPEC DO SITE — o que passa a morar no JSONB
 * ============================================================================
 *
 * Decisao da secao 08 do diagnostico: o JSONB fica, mas guarda o SPEC, nao o HTML
 * renderizado. O motivo e pratico, nao estetico:
 *
 *   - HTML renderizado e string opaca de 30-80 KB que ninguem consulta por dentro.
 *     Em jsonb ele e analisado, validado e reescrito em binario a cada escrita, vai
 *     para TOAST, e qualquer select('*') paga descompressao para devolver um nome.
 *   - Nao existe update parcial em jsonb: trocar a cor de um botao reescreve os
 *     300 KB inteiros, gera nova versao MVCC e novo trafego de WAL — que na Railway
 *     e egress faturado.
 *   - O spec tem 5-20 KB, e consultavel, versionavel e diffavel.
 *
 * E o ganho de produto que o HTML nao da: com o spec guardado, melhorar o template
 * regenera os 100 mil sites de graca. Com so o HTML, melhorar o gerador significa
 * re-rodar o Gemini em 100 mil sites.
 */
export const paletaSchema = z.object({
    primaria: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    secundaria: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    fundo: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    texto: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    destaque: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
export const tipografiaSchema = z.object({
    titulo: z.string().default('Inter'),
    corpo: z.string().default('Inter'),
    escala: z.enum(['compacta', 'normal', 'ampla']).default('normal'),
});
/**
 * Referencia de imagem. NUNCA base64.
 *
 * chaveR2 e a chave do objeto no bucket; a URL publica e derivada dela em tempo de
 * render. Guardar a chave e nao a URL e o que permite trocar o dominio do CDN sem
 * reescrever 100 mil specs.
 */
export const refImagemSchema = z.object({
    chaveR2: z.string().min(1),
    alt: z.string().default(''),
    largura: z.number().int().positive().optional(),
    altura: z.number().int().positive().optional(),
});
/**
 * Uma imagem NA BIBLIOTECA do cliente — o objeto que ja esta no R2.
 *
 * Diferente de `refImagem`, que e o USO de uma imagem dentro do spec. A mesma
 * imagem da biblioteca pode aparecer como capa e na galeria: um asset, duas
 * refs. Era isso que faltava para reaproveitar em vez de subir de novo.
 */
/**
 * Para que serve a imagem — `logo` ou foto de galeria.
 *
 * Existe porque a geracao de site trata as duas de forma diferente: a logo vira
 * o marcador `[LOGO]` no cabecalho, e as demais viram `[REMOTE_IMG_n]` na
 * galeria e nos servicos. Sem o papel gravado, a foto de perfil do WhatsApp
 * entraria como "mais uma foto" e o site sairia sem marca no topo — diferenca
 * que ninguem percebe olhando a lista de assets.
 *
 * `null` (papel nao declarado) e o caso de todo asset que ja existia e do upload
 * do editor visual: tratado como galeria na hora de gerar.
 */
export const papelAssetSchema = z.enum(['logo', 'galeria']);
export const assetSchema = z.object({
    id: z.uuid(),
    chaveR2: z.string(),
    /** URL publica ja montada pelo servidor. A tela nao concatena CDN na mao. */
    url: z.string(),
    mime: z.string(),
    bytes: z.number().int().nonnegative(),
    largura: z.number().int().nullable(),
    altura: z.number().int().nullable(),
    papel: papelAssetSchema.nullable().default(null),
    criadoEm: dataHoraSchema,
});
export const botaoSchema = z.object({
    texto: z.string(),
    href: z.string(),
    estilo: z.enum(['primario', 'secundario', 'link']).default('primario'),
});
/** Cada tipo de secao e um bloco que o renderizador sabe transformar em HTML. */
export const secaoSchema = z.discriminatedUnion('tipo', [
    z.object({
        tipo: z.literal('hero'),
        titulo: z.string(),
        subtitulo: z.string().default(''),
        imagemFundo: refImagemSchema.optional(),
        botoes: z.array(botaoSchema).default([]),
    }),
    z.object({
        tipo: z.literal('sobre'),
        titulo: z.string(),
        texto: z.string(),
        imagem: refImagemSchema.optional(),
    }),
    z.object({
        tipo: z.literal('servicos'),
        titulo: z.string(),
        itens: z.array(z.object({
            titulo: z.string(),
            descricao: z.string(),
            icone: z.string().optional(),
            preco: z.string().optional(),
            imagem: refImagemSchema.optional(),
        })),
    }),
    z.object({
        tipo: z.literal('galeria'),
        titulo: z.string().default(''),
        imagens: z.array(refImagemSchema),
    }),
    z.object({
        tipo: z.literal('depoimentos'),
        titulo: z.string(),
        itens: z.array(z.object({
            autor: z.string(),
            texto: z.string(),
            nota: z.number().min(0).max(5).optional(),
        })),
    }),
    z.object({
        tipo: z.literal('contato'),
        titulo: z.string(),
        telefone: z.string().optional(),
        whatsapp: z.string().optional(),
        email: z.email().optional(),
        endereco: z.string().optional(),
        mapaEmbedUrl: z.url().optional(),
        horarios: z.array(z.string()).default([]),
    }),
    z.object({
        tipo: z.literal('cta'),
        titulo: z.string(),
        subtitulo: z.string().default(''),
        botao: botaoSchema,
    }),
    /**
     * Escotilha de saida para o documento HTML inteiro.
     *
     * Os sites que ja existem foram gerados como HTML solto e nao tem spec. Na
     * migracao (P2 do plano) eles entram como uma unica secao htmlBruto, o que
     * permite publicar no R2 hoje sem esperar o parser que reconstroi o spec.
     *
     * `origem` diz de ONDE veio o documento, e a distincao e a razao de o campo
     * existir:
     *
     *  - `importado`     — acervo legado. Site novo nao nasce assim.
     *  - `editor-manual` — alguem escreveu o HTML na mao no editor.
     *  - `ia`            — a fabrica pediu a pagina ao modelo.
     *
     * `ia` foi acrescentado quando a esteira do worker passou a chamar o Gemini.
     * Ate entao o worker so produzia spec tipado por template, e o comentario aqui
     * dizia "sites novos nao devem usar isto" — regra que continua valendo para o
     * caminho do template. O que mudou e que a IA devolve um documento pronto, e
     * nao ha forma honesta de espremer um documento inteiro nas oito secoes
     * tipadas sem reescreve-lo. Guardar como `htmlBruto` e dizer `origem: 'ia'` e
     * a leitura verdadeira do que esta la dentro; fingir que sao secoes tipadas
     * seria a mentira.
     */
    z.object({
        tipo: z.literal('htmlBruto'),
        html: z.string(),
        origem: z.enum(['importado', 'editor-manual', 'ia']).default('importado'),
    }),
]);
export const seoSchema = z.object({
    titulo: z.string(),
    descricao: z.string().default(''),
    palavrasChave: z.array(z.string()).default([]),
    imagemOg: refImagemSchema.optional(),
    indexavel: z.boolean().default(true),
});
/**
 * ============================================================================
 * CHROME DA PAGINA — cabecalho e rodape
 * ============================================================================
 *
 * Nao sao secoes, e a distincao importa. Secao entra na ordem do array e pode
 * repetir; cabecalho e rodape existem no maximo uma vez, envolvem o resto e
 * aparecem em toda pagina. Tratar como secao convidaria a um segundo header no
 * meio do site — e o editor visual, que reordena `secoes` livremente, faria
 * isso sozinho no primeiro arrastar.
 *
 * Ambos sao OPCIONAIS: spec antigo (os que ja estao publicados) continua
 * validando e continua renderizando exatamente como antes.
 */
/** Um link do menu. `href` com `#` aponta para a ancora de uma secao. */
export const itemMenuSchema = z.object({
    texto: z.string().min(1),
    href: z.string().min(1),
});
export const cabecalhoSchema = z.object({
    /** Texto da marca. Vazio = usa `negocio.nome`. */
    marca: z.string().default(''),
    logo: refImagemSchema.optional(),
    menu: z.array(itemMenuSchema).default([]),
    /** Botao a direita do menu. Costuma ser o WhatsApp. */
    acao: botaoSchema.optional(),
    /** Gruda no topo ao rolar. */
    fixo: z.boolean().default(true),
});
export const rodapeSchema = z.object({
    /** Uma linha curta sobre o negocio, na primeira coluna. */
    sobre: z.string().default(''),
    menu: z.array(itemMenuSchema).default([]),
    redes: z
        .array(z.object({
        rede: z.enum(['instagram', 'facebook', 'whatsapp', 'youtube', 'linkedin', 'site']),
        href: z.string().min(1),
    }))
        .default([]),
    /** Linha de baixo. Vazio = "© <nome>". Sem ano: o render e deterministico. */
    assinatura: z.string().default(''),
});
/**
 * O leiaute — o que faz dois templates nao serem o mesmo site recolorido.
 *
 * Ate aqui os dez templates diferiam so em paleta e tipografia: mesma ordem,
 * mesmo CSS, mesma cara. Trocar a cor de um site nao o torna outro site. O
 * leiaute muda a ESTRUTURA que o renderizador emite — como o hero se compoe,
 * como os cards se arrumam, o que e cheio e o que e vazado.
 *
 *   - `classico`  cabecalho claro, hero centrado, cards em caixa. Seguro.
 *   - `editorial` colunas largas, hero dividido em dois, regras finas, serifa.
 *   - `vitrine`   imagem manda: hero de altura cheia, galeria em mosaico.
 *   - `moderno`   fundo escuro, gradiente, cards de vidro, cantos redondos.
 *   - `compacto`  uma pagina densa, muito conteudo em pouca rolagem.
 */
export const leiauteSchema = z.enum([
    'classico',
    'editorial',
    'vitrine',
    'moderno',
    'compacto',
]);
export const siteSpecSchema = z.object({
    /** Versao do formato do spec. Migrar template exige saber de qual versao partiu. */
    versaoSpec: z.literal(1).default(1),
    template: z.string().default('padrao-v1'),
    negocio: z.object({
        nome: z.string(),
        categoria: z.string().default(''),
        telefone: z.string().default(''),
        whatsapp: z.string().default(''),
        endereco: z.string().default(''),
        logo: refImagemSchema.optional(),
    }),
    paleta: paletaSchema,
    /** Como o renderizador monta a pagina. Default mantem o visual antigo. */
    leiaute: leiauteSchema.default('classico'),
    cabecalho: cabecalhoSchema.optional(),
    rodape: rodapeSchema.optional(),
    tipografia: tipografiaSchema.default({ titulo: 'Inter', corpo: 'Inter', escala: 'normal' }),
    seo: seoSchema,
    secoes: z.array(secaoSchema).default([]),
    /** CSS extra do editor manual. Vai para dentro de um style no fim do head. */
    cssExtra: z.string().default(''),
});
/**
 * ============================================================================
 * SITE, VERSAO E HOSTNAME
 * ============================================================================
 */
export const statusSiteSchema = z.enum(['rascunho', 'publicado', 'suspenso']);
export const siteSchema = z.object({
    id: z.uuid(),
    clienteId: z.uuid(),
    slug: slugSchema,
    status: statusSiteSchema,
    versaoAtualId: z.uuid().nullable(),
    publicadoEm: dataHoraSchema.nullable(),
    criadoEm: dataHoraSchema,
    atualizadoEm: dataHoraSchema,
    /** URL publica efetiva: dominio proprio se houver, senao slug.dominio-base. */
    urlPublica: z.string(),
});
export const versaoSiteSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    /** Chave do artefato no R2: sites/{siteId}/{versaoId}.html */
    chaveR2: z.string(),
    /** sha256 do HTML renderizado. Republicar identico nao escreve no R2. */
    hashConteudo: z.string(),
    bytes: z.number().int().nonnegative(),
    geradoPor: z.string(),
    criadoEm: dataHoraSchema,
    /** So vem preenchido em obterVersao; a listagem nao arrasta o spec. */
    spec: siteSpecSchema.optional(),
});
export const criarSiteSchema = z.object({
    clienteId: z.uuid(),
    slug: slugSchema,
    spec: siteSpecSchema,
});
export const publicarSiteSchema = z.object({
    /** Ausente = republica o spec corrente (util apos melhorar o template). */
    spec: siteSpecSchema.optional(),
    geradoPor: z.string().default('manual'),
    /** Publica sem tornar corrente. Serve para revisar antes de virar a chave. */
    apenasRascunho: z.boolean().default(false),
});
export const resultadoPublicacaoSchema = z.object({
    versaoId: z.uuid(),
    chaveR2: z.string(),
    hashConteudo: z.string(),
    bytes: z.number().int(),
    /** false quando o hash bateu com a versao corrente e nada foi escrito no R2. */
    escreveuNoR2: z.boolean(),
    urlPublica: z.string(),
});
export const statusCertificadoSchema = z.enum([
    'pendente',
    'validando',
    'ativo',
    'falhou',
    'removido',
]);
export const hostnameSchema = z.object({
    hostname: z.string(),
    siteId: z.uuid(),
    principal: z.boolean(),
    statusCertificado: statusCertificadoSchema,
    cfHostnameId: z.string().nullable(),
    verificadoEm: dataHoraSchema.nullable(),
    criadoEm: dataHoraSchema,
});
export const criarHostnameSchema = z.object({
    hostname: z
        .string()
        .min(4)
        .max(253)
        .regex(/^[a-z0-9.-]+$/, 'hostname invalido'),
    principal: z.boolean().default(false),
});
/** Resolucao usada pelo plano de dados: hostname -> onde esta o artefato. */
export const resolucaoHostnameSchema = z.object({
    siteId: z.uuid(),
    chaveR2: z.string(),
    versaoId: z.uuid(),
    hashConteudo: z.string(),
});
//# sourceMappingURL=site.js.map